import React, { useCallback, useEffect, useState } from 'react';

import { BRAILLE } from '../../symbols';
import { LETTERS } from '../../symbols/keys';
import { translate } from '../../translator';
import { GameMode } from '../../types';
import { WordListContainer } from '../../wordListContainer';
import { DrawWordCurrent, DrawWordPrevious } from './components';

const wordListContainer = new WordListContainer();

const getRandomFromArray = (arr: string[]): string => arr[Math.floor(Math.random() * arr.length)];

const getNextChallenge = (mode: GameMode): string | null => {
  switch (mode) {
    case 'draw-letter':
      return getRandomFromArray(LETTERS);
    case 'draw-word':
      return wordListContainer.getRandomWord();
    default:
      return null;
  }
};

interface DrawGameProps {
  mode: GameMode;
  onBack: () => void;
}

export const DrawGame = ({ mode, onBack }: DrawGameProps) => {
  const [currentChallenge, setCurrentChallenge] = useState<string | null>(null);
  const [drawnSymbols, setDrawnSymbols] = useState<BRAILLE[]>([]);
  const [previousResult, setPreviousResult] = useState<{ correct: string; drawnSymbols: BRAILLE[] } | null>(null);

  useEffect(() => {
    const challenge = getNextChallenge(mode);
    setCurrentChallenge(challenge);
    if (challenge !== null) {
      setDrawnSymbols(new Array(translate(challenge).length).fill(0));
    }
  }, [mode]);

  const submit = useCallback(() => {
    if (currentChallenge === null) {
      return;
    }

    setPreviousResult({ correct: currentChallenge, drawnSymbols });

    const next = getNextChallenge(mode);
    setCurrentChallenge(next);
    if (next !== null) {
      setDrawnSymbols(new Array(translate(next).length).fill(0));
    }
  }, [currentChallenge, drawnSymbols, mode]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        submit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [submit]);

  const handleSymbolChange = (index: number, value: BRAILLE) => {
    setDrawnSymbols((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex flex-col w-full justify-center mt-8">
        <DrawWordPrevious previousResult={previousResult} />
      </div>
      <div className="flex flex-col w-full items-center justify-center mt-4 overflow-x-auto mb-4">
        <DrawWordCurrent challenge={currentChallenge} drawnSymbols={drawnSymbols} onSymbolChange={handleSymbolChange} />
      </div>
      {currentChallenge !== null && (
        <button className="btn btn-wide" onClick={submit}>
          Submit
        </button>
      )}
      <button className="btn btn-ghost mt-8" onClick={onBack}>
        ← Back to mode selection
      </button>
    </div>
  );
};
